import React, { useEffect, useMemo, useState } from "react";
import GooglePosts from "./GoogleBusinessPost";
import { formatISTDate, getISTCalendarDate } from "../utils/dateTime";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
} from "recharts";

const LOCATION_ID = "4656234701670133896";

const getGBPAuthHeaders = (includeJsonContentType = false) => {
    const token = localStorage.getItem("token");

    if (!token) {
        throw new Error("Authentication token not found");
    }

    return {
        Authorization: `Bearer ${token}`,
        ...(includeJsonContentType ? { "Content-Type": "application/json" } : {}),
    };
};

const METRIC_CONFIG = {
    BUSINESS_DIRECTION_REQUESTS: {
        key: "directionRequests",
        label: "Direction Requests",
    },
    BUSINESS_IMPRESSIONS_DESKTOP_MAPS: {
        key: "desktopMaps",
        label: "Desktop Maps Impressions",
    },
    BUSINESS_IMPRESSIONS_MOBILE_MAPS: {
        key: "mobileMaps",
        label: "Mobile Maps Impressions",
    },
    BUSINESS_IMPRESSIONS_DESKTOP_SEARCH: {
        key: "desktopSearch",
        label: "Desktop Search Impressions",
    },
    BUSINESS_IMPRESSIONS_MOBILE_SEARCH: {
        key: "mobileSearch",
        label: "Mobile Search Impressions",
    },
    CALL_CLICKS: {
        key: "calls",
        label: "Calls",
    },
    WEBSITE_CLICKS: {
        key: "websiteClicks",
        label: "Website Clicks",
    },
};

const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const createEmptyDailyRecord = (date) => ({
    date,
    directionRequests: 0,
    desktopMaps: 0,
    mobileMaps: 0,
    desktopSearch: 0,
    mobileSearch: 0,
    calls: 0,
    websiteClicks: 0,
});

const formatDate = (dateObject) => {
    if (!dateObject) {
        return null;
    }

    const year = dateObject.year;
    const month = String(dateObject.month).padStart(2, "0");
    const day = String(dateObject.day).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const formatInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const formatDisplayDate = (dateString) => {
    if (!dateString) {
        return "-";
    }

    return formatISTDate(dateString, {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const GBPMatrixModal = ({ onClose, user }) => {

    const isAdmin = Number(user?.role) === 1;

    /*
     * ------------------------------------------------------------
     * TAB
     * ------------------------------------------------------------
     */

    const [activeTab, setActiveTab] = useState("matrix");

    /*
     * ------------------------------------------------------------
     * GBP MATRIX STATE
     * ------------------------------------------------------------
     */

    const today = getISTCalendarDate();

    const [startDate, setStartDate] = useState(
        formatInputDate(new Date(today.getFullYear(), today.getMonth(), 1))
    );

    const [endDate, setEndDate] = useState(
        formatInputDate(today)
    );
 

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [dailyData, setDailyData] = useState([]);

    /*
     * ------------------------------------------------------------
     * SEARCH KEYWORDS STATE
     * ------------------------------------------------------------
     */

    const [keywordYear, setKeywordYear] = useState(today.getFullYear());
    const [keywordMonth, setKeywordMonth] = useState(today.getMonth() + 1);

    const [keywordLoading, setKeywordLoading] = useState(false);
    const [keywordError, setKeywordError] = useState("");
    const [searchKeywords, setSearchKeywords] = useState([]);
    const [keywordSearch, setKeywordSearch] = useState("");

    const [showAddServiceModal, setShowAddServiceModal] = useState(false);
    const [newServiceName, setNewServiceName] = useState("");
    const [newServiceDescription, setNewServiceDescription] = useState("");
    const [addingService, setAddingService] = useState(false);
    const [addServiceError, setAddServiceError] = useState("");


    const [showEditServiceModal, setShowEditServiceModal] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [editServiceName, setEditServiceName] = useState("");
    const [editServiceDescription, setEditServiceDescription] = useState("");
    const [editingServiceIsStructured, setEditingServiceIsStructured] = useState(false);
    const [updatingService, setUpdatingService] = useState(false);
    const [editServiceError, setEditServiceError] = useState("");


    const [deletingService, setDeletingService] = useState(false);
    const [deleteServiceError, setDeleteServiceError] = useState("");

    /*
 * ------------------------------------------------------------
 * GBP PRODUCTS STATE
 * ------------------------------------------------------------
 */

    const [productsLoading, setProductsLoading] = useState(false);
    const [productsError, setProductsError] = useState("");
    const [gbpProducts, setGbpProducts] = useState([]);
    const [productsNextPageToken, setProductsNextPageToken] = useState(null);

    /*
 * ------------------------------------------------------------
 * GBP SERVICES STATE
 * ------------------------------------------------------------
 */

    const [servicesLoading, setServicesLoading] = useState(false);
    const [servicesError, setServicesError] = useState("");
    const [gbpServices, setGbpServices] = useState([]);

    const deleteGBPService = async (service) => {
        const freeForm =
            service?.freeFormServiceItem;

        const structured =
            service?.structuredServiceItem;

        const serviceName =
            freeForm?.label?.displayName ||
            structured?.serviceTypeId;

        if (!serviceName) {
            alert("Unable to identify this service.");
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to delete this service?\n\n${serviceName}`
        );

        if (!confirmed) {
            return;
        }

        setDeletingService(true);
        setDeleteServiceError("");

        try {
            const response = await fetch(
                "/v1/gbp/services/delete",
                {
                    method: "DELETE",
                    headers: getGBPAuthHeaders(true),
                    body: JSON.stringify({
                        serviceName: serviceName,
                    }),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();

                throw new Error(
                    `API request failed: ${response.status} ${errorText}`
                );
            }

            const result = await response.text();

            console.log("GBP Service deleted:", result);

            await fetchGBPServices();

        } catch (err) {
            console.error("Error deleting GBP Service:", err);

            setDeleteServiceError(
                "Unable to delete the service. Please try again."
            );
        } finally {
            setDeletingService(false);
        }
    };

    /*
 * ------------------------------------------------------------
 * GBP PRODUCTS API
 * ------------------------------------------------------------
 */

    const fetchGBPProducts = async (pageToken = null, append = false) => {
        setProductsLoading(true);
        setProductsError("");

        try {
            let url =
                `/v1/gbp/local-posts`;

            if (pageToken) {
                url += `&pageToken=${encodeURIComponent(pageToken)}`;
            }

            const response = await fetch(url, {
                headers: getGBPAuthHeaders(),
            });

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status}`);
            }

            const result = await response.json();

            const products = result?.localPosts || [];

            if (append) {
                setGbpProducts((previous) => [
                    ...previous,
                    ...products,
                ]);
            } else {
                setGbpProducts(products);
            }

            setProductsNextPageToken(
                result?.nextPageToken || null
            );

        } catch (err) {
            console.error("Error fetching GBP Products:", err);

            setProductsError(
                "Unable to fetch Google Business Profile products. Please try again."
            );
        } finally {
            setProductsLoading(false);
        }
    };

    const addGBPService = async () => {
        if (!newServiceName.trim()) {
            setAddServiceError("Service name is required.");
            return;
        }

        setAddingService(true);
        setAddServiceError("");

        try {
            const response = await fetch(
                "/v1/gbp/services/add",
                {
                    method: "POST",
                    headers: getGBPAuthHeaders(true),
                    body: JSON.stringify({
                        serviceName: newServiceName.trim(),
                        description: newServiceDescription.trim(),
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status}`);
            }

            const result = await response.json();

            console.log("GBP Service added:", result);

            // Close modal
            setShowAddServiceModal(false);

            // Clear form
            setNewServiceName("");
            setNewServiceDescription("");

            // Refresh services from Google
            await fetchGBPServices();

        } catch (err) {
            console.error("Error adding GBP Service:", err);

            setAddServiceError(
                "Unable to add the service. Please try again."
            );
        } finally {
            setAddingService(false);
        }
    };

    /*
     * ------------------------------------------------------------
     * GBP MATRIX API
     * ------------------------------------------------------------
     */

    const fetchGBPMatrix = async () => {
        if (!startDate || !endDate) {
            setError("Please select both start date and end date.");
            return;
        }

        if (startDate > endDate) {
            setError("Start date cannot be greater than end date.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const url =
                `/v1/gbp/matrix` +
                `?locationId=${LOCATION_ID}` +
                `&startDate=${startDate}` +
                `&endDate=${endDate}`;

            const response = await fetch(url, {
                headers: getGBPAuthHeaders(),
            });

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status}`);
            }

            const result = await response.json();

            const transformedData = transformGoogleResponse(result);

            setDailyData(transformedData);
        } catch (err) {
            console.error("Error fetching GBP Matrix:", err);

            setError(
                "Unable to fetch Google Business Profile metrics. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const updateGBPService = async () => {
        if (!editingService) {
            return;
        }

        if (!editServiceDescription.trim()) {
            setEditServiceError("Description is required.");
            return;
        }

        setUpdatingService(true);
        setEditServiceError("");

        try {
            const oldServiceName = editingServiceIsStructured
                ? editingService?.structuredServiceItem?.serviceTypeId
                : editingService?.freeFormServiceItem?.label?.displayName;

            const response = await fetch(
                "/v1/gbp/services/update",
                {
                    method: "PUT",
                    headers: getGBPAuthHeaders(true),
                    body: JSON.stringify({
                        oldServiceName: oldServiceName,
                        serviceName: editingServiceIsStructured
                            ? oldServiceName
                            : editServiceName.trim(),
                        description: editServiceDescription.trim(),
                    }),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();

                throw new Error(
                    `API request failed: ${response.status} ${errorText}`
                );
            }

            const result = await response.text();

            console.log("GBP Service updated:", result);

            setShowEditServiceModal(false);
            setEditingService(null);
            setEditServiceName("");
            setEditServiceDescription("");

            await fetchGBPServices();

        } catch (err) {
            console.error("Error updating GBP Service:", err);

            setEditServiceError(
                "Unable to update the service. Please try again."
            );
        } finally {
            setUpdatingService(false);
        }
    };


    /*
     * ------------------------------------------------------------
     * TRANSFORM MATRIX RESPONSE
     * ------------------------------------------------------------
     */

    const transformGoogleResponse = (response) => {
        const recordsByDate = {};

        const metricSeries =
            response?.multiDailyMetricTimeSeries?.[0]?.dailyMetricTimeSeries || [];

        metricSeries.forEach((metricSeriesItem) => {
            const metricName = metricSeriesItem.dailyMetric;
            const metricConfig = METRIC_CONFIG[metricName];

            if (!metricConfig) {
                return;
            }

            const datedValues =
                metricSeriesItem?.timeSeries?.datedValues || [];

            datedValues.forEach((datedValue) => {
                const date = formatDate(datedValue.date);

                if (!date) {
                    return;
                }

                if (!recordsByDate[date]) {
                    recordsByDate[date] = createEmptyDailyRecord(date);
                }

                recordsByDate[date][metricConfig.key] = Number(
                    datedValue.value || 0
                );
            });
        });

        return Object.values(recordsByDate).sort((a, b) =>
            a.date.localeCompare(b.date)
        );
    };

    /*
     * ------------------------------------------------------------
     * SEARCH KEYWORDS API
     * ------------------------------------------------------------
     */

    const fetchSearchKeywords = async () => {
        if (!keywordYear || !keywordMonth) {
            setKeywordError("Please select a year and month.");
            return;
        }

        setKeywordLoading(true);
        setKeywordError("");

        try {
            const url =
                `/v1/gbp/search-keywords` +
                `?year=${keywordYear}` +
                `&month=${keywordMonth}`;

            const response = await fetch(url, {
                headers: getGBPAuthHeaders(),
            });

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status}`);
            }

            const result = await response.json();

            setSearchKeywords(result?.searchKeywordsCounts || []);
        } catch (err) {
            console.error("Error fetching GBP Search Keywords:", err);

            setKeywordError(
                "Unable to fetch Google Business Profile search keywords. Please try again."
            );
        } finally {
            setKeywordLoading(false);
        }
    };

    /*
 * ------------------------------------------------------------
 * GBP SERVICES API
 * ------------------------------------------------------------
 */

    const fetchGBPServices = async () => {
        setServicesLoading(true);
        setServicesError("");

        try {
            const response = await fetch("/v1/gbp/services", {
                headers: getGBPAuthHeaders(),
            });

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status}`);
            }

            const result = await response.json();

            setGbpServices(result?.serviceItems || []);
        } catch (err) {
            console.error("Error fetching GBP Services:", err);

            setServicesError(
                "Unable to fetch Google Business Profile services. Please try again."
            );
        } finally {
            setServicesLoading(false);
        }
    };

    /*
     * ------------------------------------------------------------
     * LOAD MATRIX
     * ------------------------------------------------------------
     */

    useEffect(() => {
        fetchGBPMatrix();
    }, []);

    /*
     * ------------------------------------------------------------
     * LOAD KEYWORDS WHEN TAB IS OPENED
     * ------------------------------------------------------------
     */

    useEffect(() => {
        if (activeTab === "keywords") {
            fetchSearchKeywords();
        }

        if (activeTab === "services") {
            fetchGBPServices();
        }

        if (activeTab === "products") {
            fetchGBPProducts();
        }
    }, [activeTab]);

    /*
     * ------------------------------------------------------------
     * MATRIX SUMMARY
     * ------------------------------------------------------------
     */

    const summary = useMemo(() => {
        return dailyData.reduce(
            (totals, day) => {
                totals.directionRequests += day.directionRequests;
                totals.desktopMaps += day.desktopMaps;
                totals.mobileMaps += day.mobileMaps;
                totals.desktopSearch += day.desktopSearch;
                totals.mobileSearch += day.mobileSearch;
                totals.calls += day.calls;
                totals.websiteClicks += day.websiteClicks;

                return totals;
            },
            {
                directionRequests: 0,
                desktopMaps: 0,
                mobileMaps: 0,
                desktopSearch: 0,
                mobileSearch: 0,
                calls: 0,
                websiteClicks: 0,
            }
        );
    }, [dailyData]);

    /*
     * ------------------------------------------------------------
     * MATRIX SUMMARY CARDS
     * ------------------------------------------------------------
     */

    const summaryCards = [
        {
            title: "Total Profile Impressions",
            value:
                summary.desktopMaps +
                summary.mobileMaps +
                summary.desktopSearch +
                summary.mobileSearch,
            icon: "👁️",
        },
        {
            title: "Search Impressions",
            value: summary.desktopSearch + summary.mobileSearch,
            icon: "🔎",
        },
        {
            title: "Maps Impressions",
            value: summary.desktopMaps + summary.mobileMaps,
            icon: "📍",
        },
        {
            title: "Calls",
            value: summary.calls,
            icon: "📞",
        },
        {
            title: "Website Clicks",
            value: summary.websiteClicks,
            icon: "🌐",
        },
        {
            title: "Direction Requests",
            value: summary.directionRequests,
            icon: "🧭",
        },
    ];

    /*
     * ------------------------------------------------------------
     * CHART DATA
     * ------------------------------------------------------------
     */

    const chartData = dailyData.map((day) => ({
        date: day.date.substring(5),
        searchImpressions: day.desktopSearch + day.mobileSearch,
        mapsImpressions: day.desktopMaps + day.mobileMaps,
        calls: day.calls,
        websiteClicks: day.websiteClicks,
        directionRequests: day.directionRequests,
    }));

    /*
     * ------------------------------------------------------------
     * KEYWORD DATA
     * ------------------------------------------------------------
     */

    const readableKeywords = useMemo(() => {
        return searchKeywords
            .map((item) => {
                const exactValue = item?.insightsValue?.value;
                const threshold = item?.insightsValue?.threshold;

                return {
                    keyword: item?.searchKeyword || "",
                    value:
                        exactValue !== undefined && exactValue !== null
                            ? Number(exactValue)
                            : null,
                    threshold:
                        threshold !== undefined && threshold !== null
                            ? Number(threshold)
                            : null,
                    isThreshold:
                        exactValue === undefined &&
                        threshold !== undefined &&
                        threshold !== null,
                };
            })
            .sort((a, b) => {
                if (a.value === null && b.value === null) {
                    return a.keyword.localeCompare(b.keyword);
                }

                if (a.value === null) {
                    return 1;
                }

                if (b.value === null) {
                    return -1;
                }

                return b.value - a.value;
            });
    }, [searchKeywords]);

    /*
     * ------------------------------------------------------------
     * FILTER KEYWORDS
     * ------------------------------------------------------------
     */

    const filteredKeywords = useMemo(() => {
        const searchText = keywordSearch.trim().toLowerCase();

        if (!searchText) {
            return readableKeywords;
        }

        return readableKeywords.filter((item) =>
            item.keyword.toLowerCase().includes(searchText)
        );
    }, [readableKeywords, keywordSearch]);

    /*
     * ------------------------------------------------------------
     * KEYWORD SUMMARY
     * ------------------------------------------------------------
     */

    const exactKeywordCount = readableKeywords.filter(
        (item) => item.value !== null
    ).length;

    const thresholdKeywordCount = readableKeywords.filter(
        (item) => item.isThreshold
    ).length;

    const totalReportedSearches = readableKeywords.reduce(
        (total, item) => total + (item.value || 0),
        0
    );

    /*
     * ------------------------------------------------------------
     * RENDER
     * ------------------------------------------------------------
     */

    return (
        <div className="gbp-modal-overlay">
            <div className="gbp-modal-container">

                {/* HEADER */}

                <div className="gbp-modal-header">
                    <div>
                        <h2>Google Business Profile</h2>

                        <small>
                            Profile performance and search keywords
                        </small>
                    </div>

                    <button
                        type="button"
                        className="gbp-close-button"
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>

                {/* TABS */}

                <div className="gbp-tabs">

                    <button
                        type="button"
                        className={
                            activeTab === "matrix"
                                ? "gbp-tab active"
                                : "gbp-tab"
                        }
                        onClick={() => setActiveTab("matrix")}
                    >
                        📊 Profile Matrix
                    </button>

                    <button
                        type="button"
                        className={
                            activeTab === "keywords"
                                ? "gbp-tab active"
                                : "gbp-tab"
                        }
                        onClick={() => setActiveTab("keywords")}
                    >
                        🔎 Search Keywords
                    </button>

                    <button
                        type="button"
                        className={
                            activeTab === "posts"
                                ? "gbp-tab active"
                                : "gbp-tab"
                        }
                        onClick={() => setActiveTab("posts")}
                    >
                        📝 Google Posts
                    </button>

                    <button
                        type="button"
                        className={
                            activeTab === "services"
                                ? "gbp-tab active"
                                : "gbp-tab"
                        }
                        onClick={() => setActiveTab("services")}
                    >
                        🛠️ GBP Services
                    </button>

                    <button
                        type="button"
                        className={
                            activeTab === "products"
                                ? "gbp-tab active"
                                : "gbp-tab"
                        }
                        onClick={() => setActiveTab("products")}
                    >
                        🛍️ GBP Products
                    </button>

                </div>

                <div className="gbp-modal-body">

                    {/* ================================================== */}
                    {/* MATRIX TAB */}
                    {/* ================================================== */}

                    {activeTab === "matrix" && (
                        <>
                            <div className="gbp-filter-section">

                                <div className="gbp-filter-field">
                                    <label htmlFor="gbp-start-date">
                                        Start Date
                                    </label>

                                    <input
                                        id="gbp-start-date"
                                        type="date"
                                        value={startDate}
                                        onChange={(event) =>
                                            setStartDate(event.target.value)
                                        }
                                    />
                                </div>

                                <div className="gbp-filter-field">
                                    <label htmlFor="gbp-end-date">
                                        End Date
                                    </label>

                                    <input
                                        id="gbp-end-date"
                                        type="date"
                                        value={endDate}
                                        onChange={(event) =>
                                            setEndDate(event.target.value)
                                        }
                                    />
                                </div>

                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={fetchGBPMatrix}
                                    disabled={loading}
                                >
                                    {loading ? "Loading..." : "Apply"}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={fetchGBPMatrix}
                                    disabled={loading}
                                >
                                    ↻ Refresh
                                </button>
                            </div>

                            {loading && (
                                <div className="gbp-status-message">
                                    Fetching Google Business Profile metrics...
                                </div>
                            )}

                            {error && (
                                <div className="gbp-error-message">
                                    {error}
                                </div>
                            )}

                            {!loading &&
                                !error &&
                                dailyData.length > 0 && (
                                    <>
                                        <div className="gbp-summary-grid">
                                            {summaryCards.map((card) => (
                                                <div
                                                    className="gbp-summary-card"
                                                    key={card.title}
                                                >
                                                    <div className="gbp-summary-icon">
                                                        {card.icon}
                                                    </div>

                                                    <div>
                                                        <div className="gbp-summary-title">
                                                            {card.title}
                                                        </div>

                                                        <div className="gbp-summary-value">
                                                            {card.value.toLocaleString(
                                                                "en-IN"
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="gbp-section-card">
                                            <div className="gbp-section-heading">
                                                Daily Profile Activity
                                            </div>

                                            <div className="gbp-chart-container">
                                                <ResponsiveContainer
                                                    width="100%"
                                                    height={380}
                                                >
                                                    <LineChart data={chartData}>
                                                        <CartesianGrid strokeDasharray="3 3" />

                                                        <XAxis dataKey="date" />

                                                        <YAxis />

                                                        <Tooltip />

                                                        <Legend />

                                                        <Line
                                                            type="monotone"
                                                            dataKey="searchImpressions"
                                                            name="Search Impressions"
                                                            stroke="#2563eb"
                                                            strokeWidth={2}
                                                            dot={false}
                                                        />

                                                        <Line
                                                            type="monotone"
                                                            dataKey="mapsImpressions"
                                                            name="Maps Impressions"
                                                            stroke="#16a34a"
                                                            strokeWidth={2}
                                                            dot={false}
                                                        />

                                                        <Line
                                                            type="monotone"
                                                            dataKey="calls"
                                                            name="Calls"
                                                            stroke="#dc2626"
                                                            strokeWidth={2}
                                                            dot={false}
                                                        />

                                                        <Line
                                                            type="monotone"
                                                            dataKey="websiteClicks"
                                                            name="Website Clicks"
                                                            stroke="#9333ea"
                                                            strokeWidth={2}
                                                            dot={false}
                                                        />

                                                        <Line
                                                            type="monotone"
                                                            dataKey="directionRequests"
                                                            name="Direction Requests"
                                                            stroke="#ea580c"
                                                            strokeWidth={2}
                                                            dot={false}
                                                        />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>

                                        <div className="gbp-section-card">
                                            <div className="gbp-section-heading">
                                                Date-wise Google Business Profile Activity
                                            </div>

                                            <div className="gbp-table-wrapper">
                                                <table className="table table-bordered table-hover gbp-data-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Search Impressions</th>
                                                            <th>Maps Impressions</th>
                                                            <th>Total Impressions</th>
                                                            <th>Calls</th>
                                                            <th>Website Clicks</th>
                                                            <th>Direction Requests</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {dailyData.map((day) => {
                                                            const searchImpressions =
                                                                day.desktopSearch +
                                                                day.mobileSearch;

                                                            const mapsImpressions =
                                                                day.desktopMaps +
                                                                day.mobileMaps;

                                                            const totalImpressions =
                                                                searchImpressions +
                                                                mapsImpressions;

                                                            return (
                                                                <tr key={day.date}>
                                                                    <td>
                                                                        {formatDisplayDate(
                                                                            day.date
                                                                        )}
                                                                    </td>

                                                                    <td>
                                                                        {searchImpressions.toLocaleString(
                                                                            "en-IN"
                                                                        )}
                                                                    </td>

                                                                    <td>
                                                                        {mapsImpressions.toLocaleString(
                                                                            "en-IN"
                                                                        )}
                                                                    </td>

                                                                    <td>
                                                                        {totalImpressions.toLocaleString(
                                                                            "en-IN"
                                                                        )}
                                                                    </td>

                                                                    <td>{day.calls}</td>

                                                                    <td>{day.websiteClicks}</td>

                                                                    <td>
                                                                        {day.directionRequests}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>

                                                    <tfoot>
                                                        <tr>
                                                            <th>Total</th>

                                                            <th>
                                                                {(
                                                                    summary.desktopSearch +
                                                                    summary.mobileSearch
                                                                ).toLocaleString("en-IN")}
                                                            </th>

                                                            <th>
                                                                {(
                                                                    summary.desktopMaps +
                                                                    summary.mobileMaps
                                                                ).toLocaleString("en-IN")}
                                                            </th>

                                                            <th>
                                                                {(
                                                                    summary.desktopMaps +
                                                                    summary.mobileMaps +
                                                                    summary.desktopSearch +
                                                                    summary.mobileSearch
                                                                ).toLocaleString("en-IN")}
                                                            </th>

                                                            <th>{summary.calls}</th>

                                                            <th>{summary.websiteClicks}</th>

                                                            <th>
                                                                {summary.directionRequests}
                                                            </th>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>

                                        <div className="gbp-section-card">
                                            <div className="gbp-section-heading">
                                                Channel-wise Breakdown
                                            </div>

                                            <div className="gbp-channel-grid">

                                                <div className="gbp-channel-row">
                                                    <span>Desktop Search</span>
                                                    <strong>
                                                        {summary.desktopSearch}
                                                    </strong>
                                                </div>

                                                <div className="gbp-channel-row">
                                                    <span>Mobile Search</span>
                                                    <strong>
                                                        {summary.mobileSearch}
                                                    </strong>
                                                </div>

                                                <div className="gbp-channel-row">
                                                    <span>Desktop Maps</span>
                                                    <strong>
                                                        {summary.desktopMaps}
                                                    </strong>
                                                </div>

                                                <div className="gbp-channel-row">
                                                    <span>Mobile Maps</span>
                                                    <strong>
                                                        {summary.mobileMaps}
                                                    </strong>
                                                </div>

                                                <div className="gbp-channel-row">
                                                    <span>Calls</span>
                                                    <strong>
                                                        {summary.calls}
                                                    </strong>
                                                </div>

                                                <div className="gbp-channel-row">
                                                    <span>Website Clicks</span>
                                                    <strong>
                                                        {summary.websiteClicks}
                                                    </strong>
                                                </div>

                                                <div className="gbp-channel-row">
                                                    <span>Direction Requests</span>
                                                    <strong>
                                                        {summary.directionRequests}
                                                    </strong>
                                                </div>

                                            </div>
                                        </div>
                                    </>
                                )}

                            {!loading &&
                                !error &&
                                dailyData.length === 0 && (
                                    <div className="gbp-status-message">
                                        No Google Business Profile data found
                                        for the selected dates.
                                    </div>
                                )}
                        </>
                    )}

                    {/* ================================================== */}
                    {/* SEARCH KEYWORDS TAB */}
                    {/* ================================================== */}

                    {activeTab === "keywords" && (
                        <>
                            {/* KEYWORD FILTER BAR */}

                            <div className="gbp-keyword-filter-bar">

                                <div className="gbp-keyword-date-field">
                                    <label htmlFor="gbp-keyword-month">
                                        Month
                                    </label>

                                    <select
                                        id="gbp-keyword-month"
                                        className="gbp-keyword-select"
                                        value={keywordMonth}
                                        onChange={(event) =>
                                            setKeywordMonth(
                                                Number(event.target.value)
                                            )
                                        }
                                    >
                                        {MONTH_NAMES.map((month, index) => (
                                            <option
                                                key={month}
                                                value={index + 1}
                                            >
                                                {month}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="gbp-keyword-date-field">
                                    <label htmlFor="gbp-keyword-year">
                                        Year
                                    </label>

                                    <select
                                        id="gbp-keyword-year"
                                        className="gbp-keyword-select"
                                        value={keywordYear}
                                        onChange={(event) =>
                                            setKeywordYear(
                                                Number(event.target.value)
                                            )
                                        }
                                    >
                                        <option value={2026}>2026</option>
                                        <option value={2025}>2025</option>
                                        <option value={2024}>2024</option>
                                    </select>
                                </div>

                                <button
                                    type="button"
                                    className="btn btn-primary gbp-keyword-apply"
                                    onClick={fetchSearchKeywords}
                                    disabled={keywordLoading}
                                >
                                    {keywordLoading
                                        ? "Loading..."
                                        : "Apply"}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-outline-secondary gbp-keyword-refresh"
                                    onClick={fetchSearchKeywords}
                                    disabled={keywordLoading}
                                >
                                    ↻ Refresh
                                </button>

                            </div>

                            {keywordLoading && (
                                <div className="gbp-status-message">
                                    Fetching Google Business Profile search
                                    keywords...
                                </div>
                            )}

                            {keywordError && (
                                <div className="gbp-error-message">
                                    {keywordError}
                                </div>
                            )}

                            {!keywordLoading && !keywordError && (
                                <>
                                    {/* KEYWORD SUMMARY */}

                                    <div className="gbp-summary-grid">

                                        <div className="gbp-summary-card">
                                            <div className="gbp-summary-icon">
                                                🔎
                                            </div>

                                            <div>
                                                <div className="gbp-summary-title">
                                                    Total Keywords
                                                </div>

                                                <div className="gbp-summary-value">
                                                    {readableKeywords.length.toLocaleString(
                                                        "en-IN"
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="gbp-summary-card">
                                            <div className="gbp-summary-icon">
                                                📊
                                            </div>

                                            <div>
                                                <div className="gbp-summary-title">
                                                    Exact Values
                                                </div>

                                                <div className="gbp-summary-value">
                                                    {exactKeywordCount.toLocaleString(
                                                        "en-IN"
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="gbp-summary-card">
                                            <div className="gbp-summary-icon">
                                                🔒
                                            </div>

                                            <div>
                                                <div className="gbp-summary-title">
                                                    Below Threshold
                                                </div>

                                                <div className="gbp-summary-value">
                                                    {thresholdKeywordCount.toLocaleString(
                                                        "en-IN"
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="gbp-summary-card">
                                            <div className="gbp-summary-icon">
                                                👥
                                            </div>

                                            <div>
                                                <div className="gbp-summary-title">
                                                    Total Reported Searches
                                                </div>

                                                <div className="gbp-summary-value">
                                                    {totalReportedSearches.toLocaleString(
                                                        "en-IN"
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    {/* KEYWORD TABLE */}

                                    <div className="gbp-section-card">

                                        <div className="gbp-section-heading">
                                            Search Keywords
                                        </div>

                                        <div className="gbp-keyword-toolbar">

                                            <div className="gbp-keyword-search-box">
                                                <span>🔎</span>

                                                <input
                                                    type="text"
                                                    placeholder="Search keyword..."
                                                    value={keywordSearch}
                                                    onChange={(event) =>
                                                        setKeywordSearch(
                                                            event.target.value
                                                        )
                                                    }
                                                />
                                            </div>

                                            <span className="gbp-keyword-count">
                                                Showing{" "}
                                                {filteredKeywords.length.toLocaleString(
                                                    "en-IN"
                                                )}{" "}
                                                of{" "}
                                                {readableKeywords.length.toLocaleString(
                                                    "en-IN"
                                                )}{" "}
                                                keywords
                                            </span>

                                        </div>

                                        {filteredKeywords.length > 0 ? (
                                            <div className="gbp-table-wrapper">

                                                <table className="table table-bordered table-hover gbp-data-table">

                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: "80px" }}>
                                                                Rank
                                                            </th>

                                                            <th>
                                                                Search Keyword
                                                            </th>

                                                            <th
                                                                style={{
                                                                    width: "220px",
                                                                    textAlign: "right",
                                                                }}
                                                            >
                                                                Reported Searches
                                                            </th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>

                                                        {filteredKeywords.map(
                                                            (item, index) => (
                                                                <tr
                                                                    key={`${item.keyword}-${index}`}
                                                                >

                                                                    <td>
                                                                        {item.value !== null
                                                                            ? index + 1
                                                                            : "-"}
                                                                    </td>

                                                                    <td>
                                                                        <span className="gbp-keyword-text">
                                                                            {item.keyword}
                                                                        </span>
                                                                    </td>

                                                                    <td
                                                                        style={{
                                                                            textAlign: "right",
                                                                        }}
                                                                    >
                                                                        {item.value !== null ? (
                                                                            <span className="gbp-keyword-value">
                                                                                {item.value.toLocaleString(
                                                                                    "en-IN"
                                                                                )}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="gbp-keyword-threshold">
                                                                                Below reporting
                                                                                threshold
                                                                            </span>
                                                                        )}
                                                                    </td>

                                                                </tr>
                                                            )
                                                        )}

                                                    </tbody>

                                                </table>

                                            </div>
                                        ) : (
                                            <div className="gbp-status-message">
                                                No keywords found matching "
                                                {keywordSearch}".
                                            </div>
                                        )}

                                    </div>

                                    {/* INFORMATION */}

                                    <div className="gbp-keyword-info">

                                        <strong>
                                            About these search keywords
                                        </strong>

                                        <p>
                                            These are search terms reported by
                                            Google Business Profile for the
                                            selected month. Some keywords are
                                            shown only as being below Google's
                                            reporting threshold, so their exact
                                            count is not displayed.
                                        </p>

                                    </div>

                                </>
                            )}

                        </>
                    )}

                    {activeTab === "posts" && (
                        <GooglePosts
                            onSuccess={(message) => {
                                console.log(message);
                            }}
                            onError={(message) => {
                                console.error(message);
                            }}
                        />
                    )}

                    {/* ================================================== */}
                    {/* GBP PRODUCTS TAB */}
                    {/* ================================================== */}

                    {activeTab === "products" && (
                        <>
                            <div className="gbp-section-card">

                                <div
                                    className="gbp-section-heading"
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <span>GBP Products</span>

                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => fetchGBPProducts()}
                                        disabled={productsLoading}
                                    >
                                        {productsLoading ? "Loading..." : "↻ Refresh"}
                                    </button>
                                </div>

                                {productsLoading && (
                                    <div className="gbp-status-message">
                                        Fetching Google Business Profile products...
                                    </div>
                                )}

                                {productsError && (
                                    <div className="gbp-error-message">
                                        {productsError}
                                    </div>
                                )}

                                {!productsLoading &&
                                    !productsError &&
                                    gbpProducts.length > 0 && (
                                        <>
                                            <div className="gbp-table-wrapper">

                                                <table className="table table-bordered table-hover gbp-data-table">

                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: "60px" }}>
                                                                #
                                                            </th>

                                                            <th>
                                                                Product
                                                            </th>

                                                            <th>
                                                                Description
                                                            </th>

                                                            <th>
                                                                Price
                                                            </th>

                                                            <th>
                                                                Link
                                                            </th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>

                                                        {gbpProducts.map((product, index) => {

                                                            const productName =
                                                                product?.summary ||
                                                                product?.product?.name ||
                                                                "Unnamed Product";

                                                            const description =
                                                                product?.offer?.description ||
                                                                product?.product?.description ||
                                                                product?.description ||
                                                                "-";

                                                            const price =
                                                                product?.offer?.price ||
                                                                product?.product?.price ||
                                                                null;

                                                            const actionUrl =
                                                                product?.callToAction?.url ||
                                                                product?.offer?.url ||
                                                                null;

                                                            return (
                                                                <tr
                                                                    key={
                                                                        product?.name ||
                                                                        `${productName}-${index}`
                                                                    }
                                                                >

                                                                    <td>
                                                                        {index + 1}
                                                                    </td>

                                                                    <td>
                                                                        <strong>
                                                                            {productName}
                                                                        </strong>
                                                                    </td>

                                                                    <td
                                                                        title={description}
                                                                        style={{
                                                                            maxWidth: "400px",
                                                                            whiteSpace: "nowrap",
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                            cursor: "help",
                                                                        }}
                                                                    >
                                                                        {description}
                                                                    </td>

                                                                    <td>
                                                                        {price || "-"}
                                                                    </td>

                                                                    <td>
                                                                        {actionUrl ? (
                                                                            <a
                                                                                href={actionUrl}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                            >
                                                                                View
                                                                            </a>
                                                                        ) : (
                                                                            "-"
                                                                        )}
                                                                    </td>

                                                                </tr>
                                                            );
                                                        })}

                                                    </tbody>

                                                </table>

                                            </div>

                                            {productsNextPageToken && (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        marginTop: "20px",
                                                    }}
                                                >
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        disabled={productsLoading}
                                                        onClick={() =>
                                                            fetchGBPProducts(
                                                                productsNextPageToken,
                                                                true
                                                            )
                                                        }
                                                    >
                                                        {productsLoading
                                                            ? "Loading..."
                                                            : "Load More Products"}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}

                                {!productsLoading &&
                                    !productsError &&
                                    gbpProducts.length === 0 && (
                                        <div className="gbp-status-message">
                                            No GBP products found.
                                        </div>
                                    )}

                            </div>
                        </>
                    )}


                    {showAddServiceModal && (
                        <div
                            style={{
                                position: "fixed",
                                inset: 0,
                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 9999,
                            }}
                        >
                            <div
                                style={{
                                    background: "#fff",
                                    borderRadius: "8px",
                                    width: "500px",
                                    maxWidth: "90%",
                                    padding: "24px",
                                    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                                }}
                            >

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: "20px",
                                    }}
                                >
                                    <h4 style={{ margin: 0 }}>
                                        Add GBP Service
                                    </h4>

                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowAddServiceModal(false)}
                                    />
                                </div>

                                <div className="mb-3">

                                    <label className="form-label">
                                        Service Name
                                    </label>

                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter service name"
                                        value={newServiceName}
                                        onChange={(e) =>
                                            setNewServiceName(e.target.value)
                                        }
                                    />

                                </div>

                                <div className="mb-3">

                                    <label className="form-label">
                                        Description
                                    </label>

                                    <textarea
                                        className="form-control"
                                        rows="4"
                                        placeholder="Enter service description"
                                        value={newServiceDescription}
                                        onChange={(e) =>
                                            setNewServiceDescription(e.target.value)
                                        }
                                    />

                                </div>

                                {addServiceError && (
                                    <div
                                        className="alert alert-danger"
                                        style={{ marginBottom: "15px" }}
                                    >
                                        {addServiceError}
                                    </div>
                                )}

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: "10px",
                                    }}
                                >

                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() =>
                                            setShowAddServiceModal(false)
                                        }
                                        disabled={addingService}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={addGBPService}
                                        disabled={addingService}
                                    >
                                        {addingService
                                            ? "Adding..."
                                            : "Add Service"}
                                    </button>

                                </div>

                            </div>
                        </div>
                    )}

                    {showEditServiceModal && (
                        <div
                            style={{
                                position: "fixed",
                                inset: 0,
                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 9999,
                            }}
                        >
                            <div
                                style={{
                                    background: "#fff",
                                    borderRadius: "8px",
                                    width: "500px",
                                    maxWidth: "90%",
                                    padding: "24px",
                                    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                                }}
                            >

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: "20px",
                                    }}
                                >
                                    <h4 style={{ margin: 0 }}>
                                        Edit GBP Service
                                    </h4>

                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() =>
                                            setShowEditServiceModal(false)
                                        }
                                    />
                                </div>

                                {editingServiceIsStructured && (
                                    <div className="alert alert-info">
                                        This is a Google structured service.
                                        The service name is managed by Google and
                                        cannot be changed here.
                                    </div>
                                )}

                                <div className="mb-3">

                                    <label className="form-label">
                                        Service Name
                                    </label>

                                    <input
                                        type="text"
                                        className="form-control"
                                        value={editServiceName}
                                        disabled={editingServiceIsStructured}
                                        onChange={(e) =>
                                            setEditServiceName(e.target.value)
                                        }
                                    />

                                </div>

                                <div className="mb-3">

                                    <label className="form-label">
                                        Description
                                    </label>

                                    <textarea
                                        className="form-control"
                                        rows="5"
                                        value={editServiceDescription}
                                        onChange={(e) =>
                                            setEditServiceDescription(e.target.value)
                                        }
                                    />

                                </div>

                                {editServiceError && (
                                    <div className="alert alert-danger">
                                        {editServiceError}
                                    </div>
                                )}

                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: "10px",
                                    }}
                                >

                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() =>
                                            setShowEditServiceModal(false)
                                        }
                                        disabled={updatingService}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        disabled={updatingService}
                                        onClick={updateGBPService}
                                    >
                                        {updatingService
                                            ? "Saving..."
                                            : "Save Changes"}
                                    </button>

                                </div>

                            </div>
                        </div>
                    )}

                    {deleteServiceError && (
                        <div className="alert alert-danger">
                            {deleteServiceError}
                        </div>
                    )}

                    {activeTab === "services" && (
                        <>
                            <div className="gbp-section-card">

                                <div
                                    className="gbp-section-heading"
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <span>GBP Services</span>

                                    <div style={{ display: "flex", gap: "8px" }}>
                                        {isAdmin && (
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={() => {
                                                    setNewServiceName("");
                                                    setNewServiceDescription("");
                                                    setAddServiceError("");
                                                    setShowAddServiceModal(true);
                                                }}
                                            >
                                                + Add Service
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={fetchGBPServices}
                                            disabled={servicesLoading}
                                        >
                                            {servicesLoading ? "Loading..." : "↻ Refresh"}
                                        </button>

                                    </div>
                                </div>

                                {servicesLoading && (
                                    <div className="gbp-status-message">
                                        Fetching Google Business Profile services...
                                    </div>
                                )}

                                {servicesError && (
                                    <div className="gbp-error-message">
                                        {servicesError}
                                    </div>
                                )}

                                {!servicesLoading &&
                                    !servicesError &&
                                    gbpServices.length > 0 && (
                                        <div className="gbp-table-wrapper">

                                            <table className="table table-bordered table-hover gbp-data-table">

                                                <thead>
                                                    <tr>
                                                        <th style={{ width: "60px" }}>
                                                            #
                                                        </th>

                                                        <th>
                                                            Service
                                                        </th>

                                                        <th>
                                                            Description
                                                        </th>
                                                        {isAdmin && (
                                                            <th style={{ width: "80px" }}>
                                                                Action
                                                            </th>)}
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {gbpServices.map((service, index) => {

                                                        const freeForm =
                                                            service?.freeFormServiceItem;

                                                        const structured =
                                                            service?.structuredServiceItem;

                                                        const serviceName =
                                                            freeForm?.label?.displayName ||
                                                            structured?.serviceTypeId ||
                                                            "Unnamed Service";

                                                        const description =
                                                            freeForm?.label?.description ||
                                                            structured?.description ||
                                                            "-";

                                                        return (
                                                            <tr
                                                                key={`${serviceName}-${index}`}
                                                            >
                                                                <td>
                                                                    {index + 1}
                                                                </td>

                                                                <td>
                                                                    <strong>
                                                                        {serviceName}
                                                                    </strong>
                                                                </td>

                                                                <td
                                                                    title={description}
                                                                    style={{
                                                                        maxWidth: "400px",
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        cursor: "help",
                                                                    }}
                                                                >
                                                                    {description}
                                                                </td>
                                                                {isAdmin && (
                                                                    <td style={{ textAlign: "center" }}>
                                                                        <div
                                                                            style={{
                                                                                display: "flex",
                                                                                justifyContent: "center",
                                                                                gap: "6px",
                                                                            }}
                                                                        >
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-primary"
                                                                                title="Edit Service"
                                                                                onClick={() => {
                                                                                    const isStructured = !!structured;

                                                                                    setEditingService(service);
                                                                                    setEditingServiceIsStructured(isStructured);

                                                                                    setEditServiceName(
                                                                                        freeForm?.label?.displayName || ""
                                                                                    );

                                                                                    setEditServiceDescription(
                                                                                        freeForm?.label?.description ||
                                                                                        structured?.description ||
                                                                                        ""
                                                                                    );

                                                                                    setEditServiceError("");
                                                                                    setShowEditServiceModal(true);
                                                                                }}
                                                                            >
                                                                                ✏️
                                                                            </button>

                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-danger"
                                                                                title="Delete Service"
                                                                                disabled={deletingService}
                                                                                onClick={() => deleteGBPService(service)}
                                                                            >
                                                                                🗑️
                                                                            </button>
                                                                        </div>
                                                                    </td>)}
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>

                                            </table>

                                        </div>
                                    )}

                                {!servicesLoading &&
                                    !servicesError &&
                                    gbpServices.length === 0 && (
                                        <div className="gbp-status-message">
                                            No GBP services found.
                                        </div>
                                    )}

                            </div>
                        </>
                    )}

                </div>

                {/* FOOTER */}

                <div className="gbp-modal-footer">

                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onClose}
                    >
                        Close
                    </button>

                </div>

            </div>
        </div>
    );
};

export default GBPMatrixModal;